import { Card } from "@/components/ui/card";
import {
  LineChart as LineChartIcon,
  TrendingUp,
  ChartBar,
  Star,
  Building2,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const LenderAnalytics = () => {
  const { data: lenders } = useQuery({
    queryKey: ["lenders-analytics"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lenders")
        .select("*")
        .order("total_loans_funded", { ascending: false })
        .limit(10);

      if (error) throw error;
      return data;
    },
  });

  const performanceData = lenders?.map((lender) => ({
    name: lender.institution_name,
    loans: lender.total_loans_funded || 0,
  }));

  const activeLenders = lenders?.filter((l) => l.risk_appetite !== null).length || 0;
  const totalLoansFunded = lenders?.reduce((acc, l) => acc + (l.total_loans_funded || 0), 0) || 0;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="p-6 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-full">
              <Building2 className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Lenders</p>
              <h3 className="text-2xl font-bold">{lenders?.length || 0}</h3>
            </div>
          </div>
        </Card>

        <Card className="p-6 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-full">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Active Lenders</p>
              <h3 className="text-2xl font-bold">{activeLenders}</h3>
            </div>
          </div>
        </Card>

        <Card className="p-6 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-100 rounded-full">
              <Star className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Loans Funded</p>
              <h3 className="text-2xl font-bold">{totalLoansFunded.toLocaleString()}</h3>
            </div>
          </div>
        </Card>

        <Card className="p-6 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <ChartBar className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Average Loan Size</p>
              <h3 className="text-2xl font-bold">
                {totalLoansFunded && lenders?.length
                  ? `$${Math.round(totalLoansFunded / lenders.length).toLocaleString()}`
                  : '$0'}
              </h3>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="p-6">
          <h3 className="text-lg font-medium mb-4">Loan Performance</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="loans"
                  stroke="#8884d8"
                  name="Loans Funded"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-medium mb-4">Risk Distribution</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar
                  dataKey="loans"
                  fill="#82ca9d"
                  name="Loans"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LenderAnalytics;